import { Suspense, useMemo } from "react";

import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { PageSpinner } from "@impero-com/ui";

import { DashboardFilters as DashboardFiltersData } from "api/generated/model";
import { PageStateManager } from "org/pageState/components/pageStateManager";
import { pageStateDashboardCodecs } from "org/pageState/convert/dashboard";
import { PageStateData } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { DashboardCards } from "./components/DashboardCards";
import { DashboardCardsSkeleton } from "./components/DashboardCardsSkeleton";
import { DashboardControlActivities } from "./components/DashboardControlActivities";
import { DashboardControlProgramOverview } from "./components/DashboardControlProgramOverview";
import { DashboardFilters } from "./components/dashboardFilters/DashboardFilters";
import { TableSkeleton } from "./components/shared/tableSkeleton";
import * as theme from "./DashboardOverviewContainer.css";
import { useDashboardFilteredData } from "./hooks/useDashboardFilteredData";
import { toStrictRenderContentArgs } from "./utils/pageStateUtils";

interface DashboardOverviewContainerControlledProps {
    pageState: PageStateData<"Dashboard">;
    onPageStateChange: (newState: PageStateData<"Dashboard">) => void;
}

function DashboardOverviewContainerControlled({
    pageState,
    onPageStateChange,
}: DashboardOverviewContainerControlledProps) {
    const { t } = useTranslation();

    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const filters = useMemo(
        () =>
            controlEntitiesEnabled
                ? pageState.filters
                : { ...pageState.filters, entities: [] },
        [controlEntitiesEnabled, pageState.filters]
    );
    const onFiltersChange = (newFilters: DashboardFiltersData) => {
        onPageStateChange({
            ...pageState,
            filters: newFilters,
        });
    };

    const { isLoading, extractedTagCategories } =
        useDashboardFilteredData(filters);

    return (
        <div className={theme.root}>
            <DashboardFilters
                filters={filters}
                onFiltersChange={onFiltersChange}
                extractedTagCategories={extractedTagCategories}
                controlEntitiesEnabled={controlEntitiesEnabled}
            />

            {/* Dashboard Cards */}
            <SuspenseWithSkeleton
                isLoading={isLoading}
                fallback={<DashboardCardsSkeleton />}
            >
                <DashboardCards filters={filters} />
            </SuspenseWithSkeleton>

            {/* Overview Table */}
            <SuspenseWithSkeleton
                isLoading={isLoading}
                fallback={
                    <TableSkeleton
                        headerTitle={t(
                            "DashboardControlProgramOverview_Header"
                        )}
                        headerSubtitle={filters.timePeriod}
                    />
                }
            >
                <DashboardControlProgramOverview
                    filters={filters}
                    controlEntitiesEnabled={controlEntitiesEnabled}
                />
            </SuspenseWithSkeleton>

            {/* Latest events Table */}
            <SuspenseWithSkeleton
                isLoading={isLoading}
                fallback={
                    <TableSkeleton
                        headerTitle={t("DashboardControlActivities_Header")}
                        headerSubtitle={filters.timePeriod}
                    />
                }
            >
                <DashboardControlActivities filters={filters} />
            </SuspenseWithSkeleton>
        </div>
    );
}

interface SuspenseWithSkeletonProps {
    isLoading: boolean;
    fallback: React.ReactNode;
    children: React.ReactNode;
}

function SuspenseWithSkeleton({
    isLoading,
    fallback,
    children,
}: SuspenseWithSkeletonProps) {
    if (isLoading) {
        return <>{fallback}</>;
    }

    return <Suspense fallback={fallback}>{children}</Suspense>;
}

export function DashboardOverviewContainer() {
    return (
        <PageStateManager<"Dashboard", PageStateData<"Dashboard">>
            pageKey={PAGE_KEY.dashboard()}
            codecs={pageStateDashboardCodecs}
            renderLoading={() => <PageSpinner />}
            renderError={(code) => <LoggedInHTTPError code={code} />}
            renderContent={(renderArgs) => {
                const { pageState, onPageStateChange } =
                    toStrictRenderContentArgs(renderArgs);
                return (
                    <DashboardOverviewContainerControlled
                        pageState={pageState}
                        onPageStateChange={onPageStateChange}
                    />
                );
            }}
        />
    );
}
